/**
 * ShopClosedBanner — soft notice shown while the shop is outside working hours (ТЗ №7).
 *
 * Soft mode (the common case, ``auto`` outside hours): ordering stays available, so this is
 * purely informational — «Магазин вне рабочего времени; заказ можно оформить, продавец
 * ответит в рабочее время». The copy is server-authoritative (``me.shop_closed_notice``).
 * When the shop is open, or no notice is configured, the banner renders nothing.
 *
 * The hard-blocked case (the ``closed`` override) is handled separately: order creation then
 * fails with a 400 and the checkout screen surfaces that message inline.
 */
import type { Me } from "@entities/user";

import styles from "./ShopClosedBanner.module.css";

export interface ShopClosedBannerProps {
  readonly me: Pick<Me, "shop_is_open" | "shop_closed_notice">;
}

const ClockIcon = (): JSX.Element => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export function ShopClosedBanner({
  me,
}: ShopClosedBannerProps): JSX.Element | null {
  const notice = me.shop_closed_notice.trim();
  if (me.shop_is_open || notice.length === 0) {
    return null;
  }

  return (
    <section className={styles.banner} role="status">
      <span className={styles.icon} aria-hidden="true">
        <ClockIcon />
      </span>
      <div className={styles.text}>
        <span className={styles.title}>Магазин вне рабочего времени</span>
        <span className={styles.subtitle}>{notice}</span>
      </div>
    </section>
  );
}
